import React, { useEffect, useRef, useState } from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import Cookies from 'js-cookie';
import logo from '../../../images/bluelogo.png';
import { generatePDF } from '../pdfGenerator';
import NewAvaliacaoPDF from './newAvaliacaoPDF';
import { requestClassificacaoByProtocolo } from '../../../services/newNotesRequest';
import NewNotificacaoPreenchido from '../notificacao/newNotificacaoPreenchido';
import NewClassificacaoPreenchido from '../classificacao/newClassificacaoPreenchido';
import NewInvestigacaoPreenchido from '../investigacao/newInvestigacaoPreenchido';
import NewAvaliacaoPreenchido from './newAvaliacaoPreenchido';

/**
 * Visualização somente-leitura de uma notificação CONCLUÍDA.
 * Exibe todas as etapas preenchidas + botão de exportar PDF completo.
 *
 * Quando a notificação foi encerrada na etapa de classificação (descartar=true),
 * os formulários de investigação e avaliação final são omitidos — tanto na view
 * quanto no PDF gerado.
 */
export default function NewConcludedView({ notificacao }) {
	const _userCookie = Cookies.get('user');
	const _parsedUser = _userCookie ? JSON.parse(_userCookie) : null;
	const _isInvestigador =
		Array.isArray(_parsedUser?.permissao) &&
		_parsedUser.permissao.includes('Investigadores') &&
		!_parsedUser.permissao.includes('Classificadores');

	// Indica que a notificação foi encerrada na etapa de classificação
	// (não passou por investigação nem avaliação final)
	const [descartado, setDescartado] = useState(false);
	const [classificacao, setClassificacao] = useState(null);
	const [classificacaoCarregada, setClassificacaoCarregada] = useState(false);
	const [justificativaDescarte, setJustificativaDescarte] = useState('');
	const [pdfBaixado, setPdfBaixado] = useState(false);
	const [pdfCarregando, setPdfCarregando] = useState(false);
	const [renderPdf, setRenderPdf] = useState(false);

	const avaliacaoRef = useRef(null);
	const pdfReadyRef = useRef(false);

	const waitForPdfReady = async () => {
		const startedAt = Date.now();
		while (!pdfReadyRef.current && Date.now() - startedAt < 15000) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}
	};

	useEffect(() => {
		if (!notificacao?.protocolo) return;
		setDescartado(false);
		setClassificacao(null);
		setClassificacaoCarregada(false);
		setJustificativaDescarte('');
		setRenderPdf(false);
		pdfReadyRef.current = false;

		const userCookie = Cookies.get('user');
		if (!userCookie) return;
		const { token } = JSON.parse(userCookie);
		if (!token) return;

		let isMounted = true;

		requestClassificacaoByProtocolo(token, notificacao.protocolo)
			.then((data) => {
				if (!isMounted || !data || data.error) return;

				setClassificacao(data);

				const justificativa = String(
					data.justificativa_descarte || '',
				).trim();
				const encerradoNaClassificacao =
					data.descartar === true || Boolean(justificativa);

				if (encerradoNaClassificacao) {
					setDescartado(true);
					setJustificativaDescarte(justificativa);
				}
			})
			.catch(() => {})
			.finally(() => {
				if (isMounted) setClassificacaoCarregada(true);
			});

		return () => {
			isMounted = false;
		};
	}, [notificacao?.protocolo]);

	useEffect(() => {
		if (!notificacao?.protocolo || !classificacaoCarregada) return;
		pdfReadyRef.current = false;
		setRenderPdf(true);
	}, [
		notificacao?.protocolo,
		classificacaoCarregada,
		descartado,
		_isInvestigador,
	]);

	const handleDownloadPdf = async () => {
		setPdfCarregando(true);
		setPdfBaixado(false);
		try {
			if (!renderPdf) {
				pdfReadyRef.current = false;
				setRenderPdf(true);
				await new Promise((resolve) => requestAnimationFrame(resolve));
			}
			await waitForPdfReady();
			await new Promise((resolve) => requestAnimationFrame(resolve));
			await generatePDF(
				[avaliacaoRef],
				`${notificacao?.protocolo || 'notificacao-concluida'}.pdf`,
				{ scale: 1.35, imageQuality: 0.86 },
			);
			setPdfBaixado(true);
		} finally {
			setPdfCarregando(false);
		}
	};

	return (
		<div className='w-[95%] max-w-[1136px] font-inter mx-auto mt-[80px] flex flex-col gap-10 pb-16'>
			{/* Cabeçalho */}
			<div className='bg-[#F0F4F8] flex rounded-sm w-full content-center min-h-[142px] p-[32px] gap-[40px] justify-between lg:flex-row flex-col'>
				<div className='content-center items-center mx-auto'>
					<img
						className='w-[300px] h-auto'
						src={logo}
						alt='logo'
					/>
				</div>
				<p className='text-[32px] font-bold text-[#13335A] content-center max-w-[714px] text-center'>
					NOTIFICAÇÃO DE INCIDENTES E NÃO CONFORMIDADES
				</p>
			</div>
			{/* Etapas preenchidas */}
			<NewNotificacaoPreenchido notificacao={notificacao} />
			{descartado && (
				<section className='flex flex-col border-[1px] border-[#E3E3E3] rounded-md bg-white'>
					<p className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold font-inter border-[#E3E3E3] border-b-[1px] py-1'>
						CONCLUÍDA NA CLASSIFICAÇÃO
					</p>
					<div className='p-3 flex flex-col gap-3'>
						<p className='text-[#004A80] font-medium'>
							Esta notificacao foi concluida na etapa de
							classificacao, baseada na justificativa abaixo:
						</p>
						<div className='min-h-[120px] px-3 py-2 border-[1px] rounded-md border-[#E3E3E3] whitespace-pre-wrap break-words text-[#333333] bg-white'>
							{justificativaDescarte || 'Nao informado'}
						</div>
					</div>
				</section>
			)}
			{/* Se descartado na classificação, classificação/investigação/avaliação não são exibidas */}
			{!descartado && classificacaoCarregada && (
				<NewClassificacaoPreenchido
					notificacao={notificacao}
					classificacao={classificacao}
				/>
			)}
			{!descartado && (
				<NewInvestigacaoPreenchido notificacao={notificacao} />
			)}
			{!descartado && !_isInvestigador && (
				<NewAvaliacaoPreenchido notificacao={notificacao} />
			)}
			{/* Botão PDF */}
			<div className='w-full flex justify-start'>
				<button
					type='button'
					disabled={pdfCarregando}
					onClick={handleDownloadPdf}
					className='enabled:bg-[#E3E3E3] max-h-[40px] text-[#a3a3a3] enabled:text-[#757575] flex bg-[#d3d3d3] py-2 px-3 rounded-md enabled:hover:bg-[#757575] enabled:hover:text-[#f0f0f0] transition-all duration-200 focus:outline-none font-semibold gap-2 items-center'>
					{pdfCarregando ? (
						<>
							<span className='inline-block w-4 h-4 border-2 border-t-transparent border-current rounded-full animate-spin' />
							<span>Gerando PDF...</span>
						</>
					) : pdfBaixado ? (
						<span>Download Concluído</span>
					) : (
						<>
							<FaRegFilePdf className='w-[18px] h-auto' />
							<span>PDF</span>
						</>
					)}
				</button>
			</div>
			{/* Componente de PDF oculto — contém todas as etapas */}
			{renderPdf && (
				<div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
					<div ref={avaliacaoRef}>
						<NewAvaliacaoPDF
							notificacao={notificacao}
							hideInvestigacao={descartado}
							hideAvaliacao={descartado || _isInvestigador}
							classificacao={classificacao}
							onReady={() => {
								pdfReadyRef.current = true;
							}}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
